import React from 'react';
import { Modal } from 'react-bootstrap';

/**
 * A versatile popup component that can be used for errors, notifications, or general messages
 * Compatible with existing websites and Bootstrap styling
 */
function ErrorPopupNew({ 
  show, 
  action, 
  title = "Too Many Requests", 
  logo, 
  subtitle = "You have made too many requests. Please try again after some time or switch your internet connection.", 
  subtitleTwo, 
  buttonText, 
  showEmoji = true, 
  close = false, 
  onClose = null, 
  children, 
  centered = true 
}) {
  // Inline styles for compatibility
  const modalStyles = {
    borderRadius: '12px',
    border: 'none',
    boxShadow: '0 10px 30px rgba(0, 0, 0, 0.15)',
    overflow: 'hidden'
  };

  const headerStyles = {
    background: 'linear-gradient(rgb(71, 129, 255), rgb(103, 88, 229)) !important',
    color: 'white',
    borderBottom: 'none',
    padding: '1.5rem',
    textAlign: 'center',
    position: 'relative'
  };

  const closeButtonStyles = {
    position: 'absolute',
    top: '1rem',
    right: '1rem',
    background: 'rgba(255, 255, 255, 0.2)',
    borderRadius: '50%',
    width: '32px',
    height: '32px',
    border: 'none',
    color: 'white',
    fontSize: '18px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    opacity: '0.8',
    transition: 'opacity 0.2s ease'
  };

  const bodyStyles = {
    padding: '2rem',
    background: '#f8f9fa'
  };

  const titleStyles = {
    margin: '0',
    fontSize: '1.25rem',
    fontWeight: '600',
    textAlign: 'center'
  };

  const messageStyles = {
    textAlign: 'center',
    marginBottom: '1.5rem'
  };

  const subtitleStyles = {
    fontSize: '1rem',
    color: '#495057',
    lineHeight: '1.6',
    marginBottom: '1rem'
  };

  const subtitleTwoStyles = {
    fontSize: '0.9rem',
    color: '#6c757d',
    lineHeight: '1.5',
    marginBottom: '0'
  };

  const buttonContainerStyles = {
    display: 'flex',
    justifyContent: 'center',
    marginTop: '1.5rem'
  };

  const actionButtonStyles = {
    background: 'linear-gradient(rgb(71, 129, 255), rgb(103, 88, 229)) !important',
    border: 'none',
    borderRadius: '8px',
    padding: '0.75rem 2rem',
    fontWeight: '600',
    fontSize: '0.95rem',
    color: 'white',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    minWidth: '120px'
  };

  const logoStyles = {
    maxWidth: '48px',
    maxHeight: '48px',
    objectFit: 'contain',
    marginBottom: '0.5rem'
  };

  return (
    <>
      {/* Add CSS for hover effects */}
      <style>
        {`
          .error-popup-close-btn:hover {
            opacity: 1 !important;
            background: rgba(255, 255, 255, 0.3) !important;
          }
          
          .error-popup-action-btn:hover {
            background: linear-gradient(rgb(61, 119, 245), rgb(93, 78, 219)) !important;
            transform: translateY(-1px);
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
          }
          
          .error-popup-action-btn:focus {
            box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.25) !important;
            outline: none;
          }
          
          .error-popup-action-btn:active {
            transform: translateY(0) !important;
          }
          
          @media (max-width: 576px) {
            .error-popup-body {
              padding: 1.5rem !important;
            }
            
            .error-popup-title {
              font-size: 1.1rem !important;
            }
            
            .error-popup-subtitle {
              font-size: 0.9rem !important;
            }
          }
        `}
      </style>

      <Modal
        show={show}
        centered={centered}
        aria-labelledby="error-popup-title"
        size="md"
        backdrop="static"
        onHide={onClose}
        contentClassName="error-popup-content"
      >
        <div style={modalStyles}>
          {/* Modal Header */}
          <Modal.Header style={headerStyles}>
            {close && onClose && (
              <button
                type="button"
                className="error-popup-close-btn"
                style={closeButtonStyles}
                aria-label="Close"
                onClick={onClose}
                onMouseOver={(e) => {
                  e.target.style.opacity = '1';
                  e.target.style.background = 'rgba(255, 255, 255, 0.3)';
                }}
                onMouseOut={(e) => {
                  e.target.style.opacity = '0.8';
                  e.target.style.background = 'rgba(255, 255, 255, 0.2)';
                }}
              >
                ×
              </button>
            )}
            
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
              {logo && (
                <div style={{ marginBottom: '0.5rem' }}>
                  <img src={logo} alt="Logo" style={logoStyles} />
                </div>
              )}
              
              <h4 id="error-popup-title" className="error-popup-title" style={titleStyles}>
                {showEmoji && "⚠️ "}{title}
              </h4>
            </div>
          </Modal.Header>

          {/* Modal Body */}
          <Modal.Body className="error-popup-body" style={bodyStyles}>
            <div style={messageStyles}>
              {children ? (
                <div style={{ color: '#495057', lineHeight: '1.6' }}>
                  {children}
                </div>
              ) : (
                <div>
                  {subtitle && (
                    <p className="error-popup-subtitle" style={subtitleStyles}>
                      {subtitle}
                    </p>
                  )}
                  
                  {subtitleTwo && (
                    <p style={subtitleTwoStyles}>
                      {subtitleTwo}
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Action Button */}
            {buttonText && action && (
              <div style={buttonContainerStyles}>
                <button 
                  type="button"
                  className="error-popup-action-btn"
                  style={actionButtonStyles}
                  onClick={action}
                >
                  {buttonText}
                </button>
              </div>
            )}
          </Modal.Body>
        </div>
      </Modal>
    </>
  );
}

export default ErrorPopupNew;